import React, { useState, useEffect, useMemo } from 'react';
/* ⚠️ BU ÜÇ SATIR MODÜL ÇIKARILIRKEN DÜŞMÜŞTÜ ve `ItemTile` render edilen
   HER YERDE "Can't find variable: AnimatePresence" ile sayfayı çökertiyordu
   (mm2coinflip sayfası dahil). Sözdizimi geçerli olduğu için Babel/pyflakes
   yakalamadı; stub'lı bir SSR testi de yakalayamaz çünkü stub eksik ismi
   global olarak sağlar. Tek güvenilir kontrol: dosyanın KENDİ kapsamında
   tanımsız kalan isimleri aramak (scope.js). */
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock } from 'lucide-react';

/* ============================================================================
   MM2 EŞYA GÖRSELLERİ — PAYLAŞILAN TEK KAYNAK
   ----------------------------------------------------------------------------
   ⚠️ BU DOSYA Mm2game.js'TEN ÇIKARILDI, KOPYALANMADI.
      Aynı eşya kartları hem MM2 oyun sayfasında hem cüzdan penceresindeki
      çekim envanterinde kullanılıyor. İkinci bir kopya açılsaydı: bir
      nadirlik rengi eklendiğinde ya da görsel yükleme mantığı düzeltildiğinde
      iki dosyadan yalnızca biri güncellenir ve aynı eşya iki ekranda FARKLI
      görünürdü. Kullanıcı için bu, "site bozuk" demektir.

   ⚠️ İçerik BİREBİR taşındı — davranış değişikliği YOK. Mm2game.js bu
      tanımları artık buradan import ediyor.
============================================================================ */

/* ------------------------------------------------------------- NADİRLİK */
export const RARITY = {
  ancient:   { label: 'Ancient',   base: '#F04438', glow: 'rgba(240,68,56,.42)',   text: '#FF8A80' },
  chroma:    { label: 'Chroma',    base: '#22D3EE', glow: 'rgba(34,211,238,.42)',  text: '#7DE9FA' },
  godly:     { label: 'Godly',     base: '#F5A524', glow: 'rgba(245,165,36,.42)',  text: '#FFCB6B' },
  unique:    { label: 'Unique',    base: '#B059E8', glow: 'rgba(176,89,232,.42)',  text: '#D6A6F5' },
  legendary: { label: 'Legendary', base: '#A78BFA', glow: 'rgba(167,139,250,.38)', text: '#C9B6FD' },
  legend:    { label: 'Legend',    base: '#A78BFA', glow: 'rgba(167,139,250,.38)', text: '#C9B6FD' },
  rare:      { label: 'Rare',      base: '#3E7BFA', glow: 'rgba(62,123,250,.38)',  text: '#8FB4FF' },
  uncommon:  { label: 'Uncommon',  base: '#16A34A', glow: 'rgba(22,163,74,.38)',   text: '#5EE08F' },
  common:    { label: 'Common',    base: '#6B7280', glow: 'rgba(107,114,128,.32)', text: '#A6ADBA' },
  vintage:   { label: 'Vintage',   base: '#C08A4E', glow: 'rgba(192,138,78,.36)',  text: '#E0B784' },
  pet:       { label: 'Pet',       base: '#EC4899', glow: 'rgba(236,72,153,.38)',  text: '#F9A8D4' },
};
export const rarityOf = (r) => RARITY[String(r || '').toLowerCase()] || RARITY.common;

/* ----------------------------------------------------------- BİÇİMLEME */
export const fmtFull = (n) => Number(n || 0).toLocaleString();

/**
 * Kısa değer biçimi: 1.2M, 340K, 1.05B.
 *
 * ⚠️ BU FONKSİYON `ItemTile` TARAFINDAN KULLANILIYOR ve modül ilk
 * çıkarıldığında Mm2game.js'te UNUTULMUŞTU. Sonuç: `ItemTile` render
 * edilen her yerde "fmtVal is not defined" ile ÇÖKÜYORDU — sözdizimi
 * geçerli olduğu için hiçbir statik kontrol yakalamadı, ancak bileşeni
 * gerçekten render edince ortaya çıktı.
 * Buraya taşındı; Mm2game.js artık buradan import ediyor.
 */
export const fmtVal = (n) => {
  const raw = typeof n === 'string' ? parseFloat(n.replace(/,/g, '')) : Number(n);
  if (!raw || isNaN(raw)) return '0';
  const sign = raw < 0 ? '-' : '';
  const abs = Math.abs(raw);
  const trim = (v) => v.toFixed(2).replace(/\.?0+$/, '');
  if (abs >= 1e9) return sign + trim(abs / 1e9) + 'B';
  if (abs >= 1e6) return sign + trim(abs / 1e6) + 'M';
  if (abs >= 1e3) return sign + trim(abs / 1e3) + 'K';
  return sign + String(Math.round(abs));
};

/* --------------------------------------------------- MONOGRAM / RENK */
export const monogram = (name) => {
  const words = String(name || '?').replace(/[^A-Za-z0-9 ]/g, ' ').trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export const hueOf = (name) => {
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
};

/* --------------------------------------------------------- EŞYA GÖRSELİ */
export const ItemArt = React.memo(({ item, className = '' }) => {
  const [broken, setBroken] = useState(false);
  const rar = rarityOf(item?.rarity);

  // ⚠️ ŞEMA KONTROLÜ. Katalog ucu (MM2AdminItem) image_url için zaten https
  //    dayatıyor, ama envanter satırları o değerin KOPYASINI taşıyor ve elle
  //    DB düzenlemesiyle ya da eski kayıtlarla `data:`/`javascript:` gibi bir
  //    değer sızabilir. Tarayıcıya hiç ulaşmasın: uymayan her şey yerel
  //    monograma düşer, kullanıcı farkı görmez.
  /* ⚠️ İKİ ADAY ADRES. Kaynak eşyaları iki dizine dağıtıyor
     (mm2-items / mm2-wiki) ve ad hangisinde olduğunu söylemiyor. Sunucunun
     bilmesi için her eşya başına bir istek atması gerekirdi; bunun yerine
     iki adayı da alıp burada sırayla deniyoruz. İlki 404 verirse ikinci
     yüklenir, o da olmazsa yerel monograma düşer. */
  const ok = (u) => typeof u === 'string' && /^(https:\/\/|\/)/.test(u);
  /* Sıra: yerel kopya → kaynak dizin 1 → kaynak dizin 2.
     image_url_alt tek dize ya da dizi olabilir (sunucu sürümüne göre). */
  /* ⚠️⚠️ BAĞIMLILIK DİZE OLMALI, DİZİ DEĞİL.
     `image_url_alt` sunucudan DİZİ geliyor ve her yanıtta YENİ bir nesne.
     useEffect bağımlılığı referansa baktığı için içerik aynı olsa bile
     "değişti" sayılıyordu: her lobi güncellemesinde effect çalışıyor,
     tryIdx sıfırlanıyor, src eski değerine dönüyor ve tarayıcı görseli
     YENİDEN YÜKLÜYORDU — eşya ve avatar görsellerindeki "gidip gelme"
     buydu. Diziyi tek bir dizeye çevirince içerik değişmedikçe bağımlılık
     da değişmiyor. */
  const altKey = Array.isArray(item?.image_url_alt)
    ? item.image_url_alt.join('|')
    : String(item?.image_url_alt || '');
  const cands = useMemo(() => {
    const alts = Array.isArray(item?.image_url_alt)
      ? item.image_url_alt
      : [item?.image_url_alt];
    return [item?.image_url, ...alts].filter(ok);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.image_url, altKey]);

  const [tryIdx, setTryIdx] = useState(0);
  const url = cands[tryIdx] || null;

  useEffect(() => { setBroken(false); setTryIdx(0); }, [item?.image_url, altKey]);

  const onFail = () => {
    if (tryIdx + 1 < cands.length) setTryIdx(tryIdx + 1);
    else setBroken(true);
  };

  if (url && !broken) {
    return (
      <img
        src={url}
        alt={item?.name || ''}
        loading="lazy"
        draggable={false}
        onError={onFail}
        className={`w-full h-full object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,.55)] ${className}`}
      />
    );
  }

  const hue = hueOf(item?.name);
  return (
    <div
      className={`w-full h-full rounded-lg flex items-center justify-center select-none ${className}`}
      style={{
        background: `radial-gradient(120% 120% at 30% 20%, hsl(${hue} 42% 26%) 0%, hsl(${hue} 38% 12%) 62%, #0d0f12 100%)`,
        boxShadow: `inset 0 0 0 1px ${rar.base}33, inset 0 12px 26px -14px ${rar.base}66`,
      }}
    >
      <span
        className="font-heading font-black tracking-tight"
        style={{
          // ⚠️ Container query (cqw) KULLANILMIYOR. Desteklemeyen eski
          //    webview'lerde clamp() tamamen geçersiz olur ve yazı devralınan
          //    boyuta düşerek monogramı taşırır. Sabit boyut her yerde çalışır;
          //    kart 36–56px arasında olduğu için 15px iki harfe de yeter.
          fontSize: 15,
          color: rar.base,
          textShadow: `0 0 14px ${rar.glow}`,
          opacity: 0.92,
        }}
      >
        {monogram(item?.name)}
      </span>
    </div>
  );
});

/* ----------------------------------------------------------- EŞYA KARTI */
export const ItemTile = React.memo(({ item, selected, disabled, onClick, showState, compact }) => {
  const rar = rarityOf(item?.rarity);
  const locked = showState && item?.state && item.state !== 'available';
  const isDisabled = disabled || locked;

  return (
    <button
      type="button"
      onClick={isDisabled ? undefined : () => onClick?.(item?.id)}
      disabled={isDisabled}
      title={`${item?.name} — ${fmtFull(item?.value)}`}
      className={`group relative rounded-xl border text-left transition-all duration-200
        ${compact ? 'p-1.5' : 'p-2'}
        ${isDisabled
          ? 'opacity-40 cursor-not-allowed border-[#2A2D31] bg-[#121417]'
          : selected
            ? 'border-transparent bg-[#191c20] -translate-y-0.5 cursor-pointer'
            : 'border-[#2A2D31] bg-[#15171a] hover:border-[#3c4046] hover:-translate-y-0.5 cursor-pointer'}`}
      style={{
        boxShadow: selected
          ? `0 0 0 2px ${rar.base}, 0 10px 26px -10px ${rar.glow}`
          : undefined,
      }}
    >
      {/* nadirlik ışıması — seçiliyken güçlenir */}
      <div
        className="pointer-events-none absolute inset-0 rounded-xl transition-opacity duration-200"
        style={{
          background: `radial-gradient(90% 70% at 50% 0%, ${rar.base}22 0%, transparent 70%)`,
          opacity: selected ? 1 : 0.45,
        }}
      />

      {/* ⚠️ GÖRSEL BÜYÜTÜLDÜ. compact modda 44px idi ve eşya tanınmıyordu;
          envanter bir seçim ekranı, kullanıcı neyi seçtiğini GÖRMELİ. */}
      <div className={`relative ${compact ? 'h-16' : 'h-20'} mb-1.5`}>
        <ItemArt item={item} />
      </div>

      <div className="relative">
        {/* ⚠️ AD İKİ SATIRA KADAR. Tek satır + truncate ile çoğu ad
            "Chroma Vampire's…" diye kesiliyordu. İki satır, adların
            büyük çoğunluğunu tam gösteriyor; gerçekten uzun olanlar
            yine kırpılıyor ama istisna oluyor. */}
        <p className={`font-semibold text-white/90 leading-tight line-clamp-2 min-h-[2.1em] ${
          compact ? 'text-[11px]' : 'text-[12px]'}`}>
          {item?.name}
        </p>
        <div className="flex items-center justify-between gap-1 mt-0.5">
          <span
            className="text-[9px] font-bold uppercase tracking-wider truncate"
            style={{ color: rar.text }}
          >
            {rar.label}
          </span>
          <span className={`font-heading font-black text-white shrink-0 ${compact ? 'text-[11px]' : 'text-[12px]'}`}>
            {fmtVal(item?.value)}
          </span>
        </div>
      </div>

      {/* kilitli durum rozeti */}
      {locked && (
        <div className="absolute inset-0 rounded-xl bg-[#0E0F11]/75 flex flex-col items-center justify-center gap-1">
          <Lock className="w-3.5 h-3.5 text-white/70" />
          <span className="text-[8px] font-bold uppercase tracking-widest text-white/70">
            {item.state === 'withdrawing' ? 'Withdrawing' : 'In game'}
          </span>
        </div>
      )}

      {/* seçim tiki */}
      <AnimatePresence>
        {selected && !isDisabled && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 520, damping: 24 }}
            className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
            style={{ background: rar.base, boxShadow: `0 0 12px ${rar.glow}` }}
          >
            <Check className="w-3 h-3 text-black" strokeWidth={3.5} />
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
});

/* ============================================================================
   MADENİ PARA  —  SAYFANIN İMZA ÖĞESİ
   ============================================================================
   GERÇEK 3B. Sahte "flip" (iki görselin arasında geçiş) değil:
     • ön yüz  translateZ(+T/2)
     • arka yüz rotateY(180) translateZ(+T/2)
     • kenar   36 dilim; her biri rotateZ(φ) translateX(R) rotateY(90)
       → silindirin yan yüzeyi. Para yandan bakıldığında GERÇEK KALINLIĞA
         sahip; işlenmiş metal kenar (reeding) bu yüzden görünür.

   İNİŞ MATEMATİĞİ
   ---------------
   Hedef açı DAİMA artar (asla geri dönmez):
       hedef = mevcut + 7 tam tur, sonra kazanan yüze (0° / 180°) yuvarla
   Sunucu flip için 5sn ayırıyor (MM2_ANIM_FLIP_SEC); animasyon 4.6sn'de
   biter → sonuç mesajı geldiğinde para ÇOKTAN oturmuş olur, "önce sonuç
   yazısı sonra dönüş" gibi bir tutarsızlık oluşmaz.

   ⚠️ prefers-reduced-motion: dönüş atlanır, doğrudan kazanan yüze oturur.
============================================================================ */

// Sheriff rozeti — 6 köşeli yıldız köşe noktaları (bir kez hesaplanır)
/* ============================================================================
   MADENİ PARA — iki PNG yüz, gerçek 3B çevirme

   ⚠️ Önceki sürüm parayı CSS ile ÇİZİYORDU: 36 kenar dilimi, yıldız çokgeni,
      gradyanlar ve blur filtresi. Dönerken her karede yeniden boyanıyor ve
      bulanıklaşıyordu. Artık iki hazır görsel var ve dönüş yalnızca
      `rotateY` — GPU'da çalışır, hiçbir şey yeniden boyanmaz, görsel
      keskin kalır.

   ⚠️ `backface-visibility: hidden` iki yüzü ayırır: arka yüz kendi
      rotateY(180deg) ile yerleştirilir, böylece para döndükçe doğru görsel
      görünür. Bu olmadan iki görsel üst üste biner ve saydam görünürdü.
============================================================================ */
